/**
 * metrics-nested-rows.tsx — 转化 / 留存看板「分组逐级嵌套」共用的行树与首列。
 *
 * 选了多个分组维度时，第一个维度是顶层行，后一个维度挂在前一个下面，可逐级展开（如 语言 → 激活日期）。
 * 每一级的行来自该前缀的独立查询（见 `~/data/metrics-nested-levels`），这里按维度前缀把下一级行挂到父行下。
 */

import type { ReactNode } from 'react';
import { ExpandToggleButton } from '~/ui/ExpandToggleButton';

export type NestedRowDims = { dims: Record<string, string | null | undefined> };

export interface NestedLevelRows<R> {
  rows: R[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

export type NestedFlatEntry<R> =
  | {
      kind: 'row';
      row: R;
      depth: number;
      key: string;
      expandable: boolean;
      open: boolean;
    }
  | { kind: 'placeholder'; depth: number; key: string; state: 'loading' | 'error' | 'empty' };

/** 行键 = 到本级为止的维度值前缀（JSON），长度即深度；跨级唯一 */
export function nestedRowKey(dims: readonly string[], row: NestedRowDims, depth: number): string {
  return JSON.stringify(dims.slice(0, depth + 1).map((dimension) => row.dims[dimension] ?? null));
}

/** 展开键（维度值前缀的 JSON）里最长的前缀长度 */
export function nestedDeepestKeyLength(expanded: ReadonlySet<string>): number {
  let deepest = 0;
  for (const key of expanded) {
    try {
      const parsed: unknown = JSON.parse(key);
      if (Array.isArray(parsed)) deepest = Math.max(deepest, parsed.length);
    } catch {
      // 非本组件生成的键直接忽略
    }
  }
  return deepest;
}

/** 展开键里最深的一级决定要发到第几级查询（第 1 级恒发，返回「更深级」个数） */
export function nestedActiveDeeperLevels(expanded: ReadonlySet<string>, dimCount: number): number {
  return Math.min(nestedDeepestKeyLength(expanded), Math.max(dimCount - 1, 0));
}

/** 切换展开键（返回新集合）；用过滤 / 追加实现，避免 admin-only 写入闸把 Set.delete 误判为写请求 */
export function toggleNestedKey(keys: ReadonlySet<string>, key: string): Set<string> {
  return keys.has(key)
    ? new Set([...keys].filter((item) => item !== key))
    : new Set([...keys, key]);
}

/** 第 2 级起的 group_by 前缀：[d1,d2]、[d1,d2,d3]… */
export function nestedDeeperGroupBys<D extends string>(dims: readonly D[]): D[][] {
  return dims.slice(1).map((_, index) => dims.slice(0, index + 2));
}

const sameValue = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? null) === (b ?? null);

/** 把各级行摊平成「当前可见」的行序列：顶层行 → 已展开行的下一级行 → …… */
export function flattenNestedRows<R extends NestedRowDims>(args: {
  dims: readonly string[];
  /** levels[0] = 第 1 级，levels[k] = group_by 为前 k+1 个维度的结果 */
  levels: ReadonlyArray<NestedLevelRows<R>>;
  expanded: ReadonlySet<string>;
  sortRows: (rows: R[], depth: number) => R[];
}): Array<NestedFlatEntry<R>> {
  const { dims, levels, expanded, sortRows } = args;
  const out: Array<NestedFlatEntry<R>> = [];
  const visit = (depth: number, parent: R | null) => {
    const level = levels[depth];
    const all = level?.rows ?? [];
    const siblings = parent
      ? all.filter((row) =>
          dims.slice(0, depth).every((d) => sameValue(row.dims[d], parent.dims[d])),
        )
      : all;
    for (const row of sortRows(siblings, depth)) {
      const key = nestedRowKey(dims, row, depth);
      const expandable = depth < dims.length - 1;
      const open = expandable && expanded.has(key);
      out.push({ kind: 'row', row, depth, key, expandable, open });
      if (!open) continue;
      const next = levels[depth + 1];
      if (!next || next.rows === undefined) {
        out.push({
          kind: 'placeholder',
          depth: depth + 1,
          key: `${key}:state`,
          state: next?.isError ? 'error' : 'loading',
        });
        continue;
      }
      const before = out.length;
      visit(depth + 1, row);
      if (out.length === before)
        out.push({ kind: 'placeholder', depth: depth + 1, key: `${key}:state`, state: 'empty' });
    }
  };
  visit(0, null);
  return out;
}

/** 行首列：按深度缩进 + 展开按钮（末级无按钮，留同宽占位对齐） */
export function NestedLabelCell({
  depth,
  expandable,
  open,
  onToggle,
  toggleLabel,
  children,
}: {
  depth: number;
  expandable: boolean;
  open: boolean;
  onToggle: () => void;
  toggleLabel: string;
  children: ReactNode;
}) {
  return (
    <span className="hstack" style={{ gap: 4, paddingLeft: depth * 20, whiteSpace: 'nowrap' }}>
      {expandable ? (
        <ExpandToggleButton size={22} expanded={open} onToggle={onToggle} label={toggleLabel} />
      ) : (
        <span aria-hidden="true" style={{ display: 'inline-block', width: 22, flexShrink: 0 }} />
      )}
      {children}
    </span>
  );
}

export const NESTED_PLACEHOLDER_TEXT: Record<'loading' | 'error' | 'empty', string> = {
  loading: '正在加载明细…',
  error: '明细加载失败，请折叠后重试',
  empty: '该行没有下一级明细',
};
